import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { ShoppingBag, Trash2 } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function CartDrawer() {
  const { items, count, usdTotal, update, remove } = useCart();
  const { format } = useCurrency();

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button data-testid="cart-btn" className="relative p-2 rounded-full hover:bg-stone-100 transition-colors">
          <ShoppingBag className="w-5 h-5 text-stone-700"/>
          {count > 0 && (
            <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-[#D97757] text-white text-[10px] font-semibold rounded-full w-4 h-4 flex items-center justify-center">
              {items.length}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="bg-white w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle>Your portfolio</SheetTitle>
        </SheetHeader>
        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-stone-500">
            <ShoppingBag className="w-8 h-8 mb-3 opacity-40"/>
            <div className="text-sm">Cart is empty</div>
            <Link to="/marketplace"><Button variant="ghost" className="mt-3 text-[#2C5530]">Browse projects</Button></Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto -mx-6 px-6 divide-y divide-stone-200">
              {items.map((i) => (
                <div key={i.project_id} data-testid={`cart-item-${i.project_id}`} className="py-4 flex gap-3">
                  <img src={i.image_url} alt="" className="w-14 h-14 rounded-xl object-cover"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{i.name}</div>
                    <div className="text-xs text-stone-500 mt-0.5">{format(i.price_per_ton)}/t</div>
                    <div className="flex items-center gap-2 mt-2">
                      <Input type="number" min={1} max={i.available_tons} value={i.tons} onChange={(e) => update(i.project_id, e.target.value)} className="h-8 w-20 text-sm"/>
                      <span className="text-xs text-stone-500">tons</span>
                      <button onClick={() => remove(i.project_id)} className="ml-auto text-stone-400 hover:text-red-500" data-testid={`cart-remove-${i.project_id}`}>
                        <Trash2 className="w-4 h-4"/>
                      </button>
                    </div>
                  </div>
                  <div className="text-sm font-medium">{format(i.price_per_ton * i.tons)}</div>
                </div>
              ))}
            </div>
            <div className="border-t border-stone-200 pt-4 mt-4">
              <div className="flex justify-between mb-4">
                <span className="text-sm text-stone-600">Subtotal</span>
                <span className="text-2xl font-light" data-testid="cart-subtotal">{format(usdTotal)}</span>
              </div>
              <Link to="/cart">
                <Button data-testid="cart-checkout-btn" className="w-full h-11 bg-[#2C5530] hover:bg-[#1F3D22] text-white rounded-full">
                  Review & checkout
                </Button>
              </Link>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
